import React, { useEffect, useState } from "react";
import { Download, Globe, Loader2, Mail, RefreshCw, RotateCcw, Search, ShieldCheck, ShieldX } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, downloadFile } from "../api";
import { fmtDate, useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { SOURCE_CODE_LOGOS } from "../landing_i18n";
import { EmailReportButton } from "../components/EmailReportButton";
import VerifEditor from "../components/VerifEditor";

const SRC_LABEL = { OFAC: "OFAC (SDN)", UN: "CSNU (ONU)", FATF: "GAFI (FATF)", FBI: "FBI Most Wanted", INTERPOL: "INTERPOL", WB: "Banco Mundial", NAMUS: "NamUs", VESSEL: "OFAC Vessel" };
const ALL_SOURCES = ["OFAC", "UN", "FATF", "FBI", "INTERPOL", "WB", "NAMUS", "VESSEL"];
const SEPARATE = ["FBI", "INTERPOL", "WB", "NAMUS"];

function SourcePicker({ sources, setSources, visible = ALL_SOURCES, full = false }) {
  const toggle = (s) => setSources(sources.includes(s) ? sources.filter((x) => x !== s) : [...sources, s]);
  return (
    <div className={full ? "grid grid-cols-2 sm:grid-cols-3 gap-2 w-full" : "flex flex-wrap gap-2"}>
      {visible.map((s) => (
        <button
          key={s}
          type="button"
          data-testid={`source-${s}`}
          onClick={() => toggle(s)}
          className={`${full ? "w-full" : ""} px-4 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
            sources.includes(s) ? "bg-[#16335F] text-white border-[#16335F]" : "bg-white text-slate-500 border-slate-300"
          }`}
        >
          {SRC_LABEL[s]}
        </button>
      ))}
    </div>
  );
}

function ResultBanner({ r, t, onNew, moduleLabel }) {
  const match = r.result === "match";
  return (
    <div className={`flex flex-wrap items-center gap-3 rounded-lg px-4 py-3 border ${match ? "bg-red-50 border-red-200" : "bg-emerald-50 border-emerald-200"}`} data-testid="search-result-banner">
      {match ? <ShieldX className="w-6 h-6 text-red-600" /> : <ShieldCheck className="w-6 h-6 text-emerald-600" />}
      <div className="flex-1 min-w-[180px]">
        <div className={`font-heading font-extrabold ${match ? "text-red-700" : "text-emerald-700"}`} data-testid="search-result-text">
          {match ? t("search.match") : t("search.no_match")}
        </div>
        <div className="text-xs text-slate-600">
          {t("search.consult_no")} <span className="font-mono font-semibold" data-testid="consult-number">{r.consult_number}</span>
          {match && ` · ${r.match_count} ${t("search.matches_found")}`}
        </div>
      </div>
      <button
        onClick={onNew}
        data-testid="new-search-button"
        className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-slate-300 bg-white text-xs font-bold text-slate-700 hover:border-slate-500 transition-colors"
      >
        <RotateCcw className="w-3.5 h-3.5" /> {t("search.new")}
      </button>
      <EmailReportButton pdfPath={`/sanctions/searches/${r.id}/pdf`} filename={`consulta_${r.consult_number}.pdf`} subject={`Reporte de consulta en listas ${r.consult_number}`} module={moduleLabel} summary={`${r.query} — ${r.match_count} coincidencia(s)`} testid="email-search-report" />
      <button
        onClick={() => downloadFile(`/sanctions/searches/${r.id}/pdf`, `consulta_${r.consult_number}.pdf`)}
        data-testid="download-search-pdf"
        className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#16335F] text-white text-xs font-bold hover:bg-[#033A8C] transition-colors"
      >
        <Download className="w-3.5 h-3.5" /> {t("common.download_pdf")}
      </button>
    </div>
  );
}

export default function SanctionsSearch({ fixed = null, titleKey = "search.title", subtitleKey = "search.subtitle", showOsint = false }) {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const hasVessels = !user?.tenant_modules || user.tenant_modules.includes("vessels");
  const VISIBLE = fixed ? [fixed] : ALL_SOURCES.filter((s) => !SEPARATE.includes(s) && (s !== "VESSEL" || hasVessels));
  const [tab, setTab] = useState("individual");
  const [status, setStatus] = useState(null);
  const [query, setQuery] = useState("");
  const [entityType, setEntityType] = useState("all");
  const [sources, setSources] = useState(fixed ? [fixed] : ALL_SOURCES.filter((s) => !SEPARATE.includes(s)));
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkResults, setBulkResults] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);

  const newSearch = () => { setResult(null); setQuery(""); setBulkResults([]); setBulkText(""); };

  const loadStatus = () => api.get("/sanctions/status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { loadStatus(); }, []);

  const refresh = async () => {
    try {
      await api.post("/sanctions/refresh");
      toast.success(t("search.refresh_started"));
      setTimeout(loadStatus, 5000);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const runSearch = async (e) => {
    e.preventDefault();
    if (!query.trim() || sources.length === 0) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/sanctions/search", { query: query.trim(), entity_type: entityType, sources });
      setResult(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const runBulk = async () => {
    const queries = bulkText.split("\n").map((s) => s.trim()).filter((s) => s.length >= 3);
    if (queries.length === 0 || sources.length === 0) return;
    setBulkBusy(true);
    setBulkResults([]);
    try {
      const { data } = await api.post("/sanctions/search/bulk", { queries, entity_type: entityType, sources });
      setBulkResults(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid={fixed ? `sanctions-page-${fixed}` : "sanctions-page"}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{t(titleKey)}</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">{t(subtitleKey)}</p>
        </div>
        {user?.role === "admin" && (
          <button onClick={refresh} data-testid="refresh-lists-button" className="flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 bg-white text-sm font-semibold text-slate-700 hover:border-slate-500 transition-colors">
            <RefreshCw className="w-4 h-4" /> {t("search.refresh")}
          </button>
        )}
      </div>

      {status && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {VISIBLE.filter((src) => src !== "VESSEL").map((src) => {
            const s = status[src] || {};
            return (
              <div key={src} className="ec-card px-4 py-2.5" data-testid={`status-card-${src}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 truncate">{SRC_LABEL[src]}</span>
                  <span className={`shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full ${s.status === "ok" ? "bg-emerald-50 text-emerald-700" : s.status === "error" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>
                    {s.status === "ok" ? t("search.status_ok") : s.status === "error" ? t("search.status_error") : t("search.status_pending")}
                  </span>
                </div>
                <div className="font-heading text-base font-extrabold text-slate-900 mt-0.5">{(s.count || 0).toLocaleString()} <span className="text-[11px] font-semibold text-slate-500">{t("search.entries")}</span></div>
                <div className="text-[10px] text-slate-400 truncate">{t("search.last_update")}: {s.last_update ? fmtDate(s.last_update) : "—"}</div>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex rounded-lg bg-slate-200/70 p-1 w-fit">
        <button data-testid="tab-individual" onClick={() => setTab("individual")} className={`px-5 py-2 text-sm font-semibold rounded-md transition-colors ${tab === "individual" ? "bg-[#16335F] text-white" : "text-slate-600"}`}>
          {t("search.individual")}
        </button>
        <button data-testid="tab-bulk" onClick={() => setTab("bulk")} className={`px-5 py-2 text-sm font-semibold rounded-md transition-colors ${tab === "bulk" ? "bg-[#16335F] text-white" : "text-slate-600"}`}>
          {t("search.bulk")}
        </button>
      </div>

      {tab === "individual" ? (
        <div className="ec-card p-6 space-y-5" data-testid="individual-search-panel">
          <form onSubmit={runSearch} className="space-y-6">
            <div className="grid md:grid-cols-[1fr_240px_auto] gap-5 items-end">
              <div>
                <label className="ec-label">{t("search.name_label")}</label>
                <input className="ec-input !text-lg !py-3.5 font-medium" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("search.name_ph")} data-testid="ofac-search-input" />
              </div>
              <div>
                <label className="ec-label">{t("search.type_label")}</label>
                <select className="ec-input !text-base !py-3.5 font-medium" value={entityType} onChange={(e) => setEntityType(e.target.value)} data-testid="entity-type-select">
                  <option value="all">{t("search.t_all")}</option>
                  <option value="aircraft">{t("search.t_aircraft")}</option>
                  <option value="entity">{t("search.t_entity")}</option>
                  <option value="individual">{t("search.t_individual")}</option>
                  <option value="vessel">{t("search.t_vessel")}</option>
                </select>
              </div>
              <button type="submit" disabled={busy} data-testid="search-submit-button" className="flex items-center justify-center gap-2 px-8 py-3.5 rounded-lg bg-[#044CBC] text-white text-base font-extrabold shadow-lg shadow-orange-300/50 hover:bg-[#033A8C] hover:shadow-xl hover:shadow-orange-300/60 hover:-translate-y-0.5 transition-all disabled:opacity-60 whitespace-nowrap">
                {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
                {busy ? t("search.running") : t("search.run")}
              </button>
            </div>
            {!fixed && (
            <div>
              <label className="ec-label">{t("search.sources")}</label>
              <SourcePicker sources={sources} setSources={setSources} visible={VISIBLE} full />
            </div>
            )}
          </form>
          {!fixed && result && (
            <button onClick={() => navigate("/osint", { state: { name: result.query, searchId: result.id, review: result.osint_review || null } })} data-testid="osint-open-button"
                    className="w-full ec-card p-4 flex items-center justify-between gap-3 hover:shadow-md transition-shadow text-left">
              <span className="flex items-center gap-2 font-heading font-bold text-slate-900"><Globe className="w-5 h-5 text-[#044CBC]" /> {t("osint.title")}</span>
              <span className="text-xs font-bold text-[#044CBC]">{t("osint.open_for")} {result.query} →</span>
            </button>
          )}

          {result && (
            <div className="space-y-4">
              <ResultBanner r={result} t={t} onNew={newSearch} moduleLabel={t(titleKey)} />
              <VerifEditor endpoint={`/sanctions/searches/${result.id}`} doc={result} testid="sv" onSaved={(v) => setResult({ ...result, ...v })} />
              {result.matches?.length > 0 && (
                <div className="overflow-x-auto rounded-lg border border-slate-200" data-testid="matches-table">
                  <table className="w-full text-sm min-w-[720px]">
                    <thead>
                      <tr className="bg-[#16335F] text-white text-left">
                        <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("search.col_list")}</th>
                        <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("search.col_name")}</th>
                        <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("search.col_type")}</th>
                        <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("search.col_program")}</th>
                        <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("search.col_ref")}</th>
                        <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide text-right">{t("search.col_score")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.matches.map((m, i) => (
                        <tr key={i} className="border-t border-slate-100 hover:bg-slate-50">
                          <td className="px-3 py-2.5"><span className="inline-flex items-center gap-1.5">{SOURCE_CODE_LOGOS[m.source] && <img src={SOURCE_CODE_LOGOS[m.source]} alt="" className="h-5 w-5 object-contain shrink-0" data-testid={`match-source-logo-${i}`} />}<span className={`text-[11px] font-bold px-2 py-0.5 rounded ${m.source === "INTERNA" ? "bg-[#044CBC] text-white" : m.source === "OFAC" ? "bg-orange-100 text-[#033A8C]" : m.source === "FBI" ? "bg-slate-800 text-white" : m.source === "FATF" ? "bg-orange-100 text-orange-800" : m.source === "INTERPOL" ? "bg-sky-100 text-sky-800" : m.source === "WB" ? "bg-emerald-100 text-emerald-800" : m.source === "NAMUS" ? "bg-purple-100 text-purple-800" : m.source === "VESSEL" ? "bg-cyan-100 text-cyan-900" : "bg-indigo-100 text-indigo-800"}`}>{m.source === "UN" ? "CSNU" : m.source === "WB" ? "Banco Mundial" : m.source === "NAMUS" ? "NamUs" : m.source === "VESSEL" ? "OFAC Vessel" : m.source}</span></span></td>
                          <td className="px-3 py-2.5 font-semibold text-slate-800">
                            {m.name}
                            {m.matched_alias && <div className="text-xs font-normal text-amber-700">{t("search.alias")}: {m.matched_alias}</div>}
                            {m.source === "INTERPOL" && (m.nationality || m.dob) && (
                              <div className="text-xs font-normal text-slate-500" data-testid="interpol-detail">
                                {[m.nationality && `${t("search.nationality")}: ${m.nationality}`, m.dob && `${t("search.dob")}: ${m.dob}`].filter(Boolean).join(" · ")}
                              </div>
                            )}
                            {m.source === "NAMUS" && (
                              <div className="text-xs font-normal text-slate-500" data-testid="namus-detail">
                                {[m.missing_date && `${t("search.missing_date")}: ${m.missing_date}`,
                                  (m.state || m.city) && `${t("search.state_country")}: ${[m.city, m.state].filter(Boolean).join(", ")}, EE.UU.`,
                                  m.age && `${t("search.age")}: ${m.age}`,
                                  m.gender && `${t("search.gender")}: ${m.gender}`].filter(Boolean).join(" · ")}
                              </div>
                            )}
                            {m.source === "VESSEL" && (
                              <div className="text-xs font-normal text-slate-500" data-testid="vessel-detail">
                                {[m.vessel_type && `${t("vs.type")}: ${m.vessel_type}`, m.flag && `${t("vs.flag")}: ${m.flag}`,
                                  m.imo && `IMO: ${m.imo}`, m.sanction_date && `${t("vs.sanction_date")}: ${m.sanction_date}`].filter(Boolean).join(" · ")}
                              </div>
                            )}
                            {m.source === "WB" && (
                              <div className="text-xs font-normal text-slate-500" data-testid="wb-detail">
                                {[m.country && `${t("search.country")}: ${m.country}`,
                                  m.grounds && `${t("search.grounds")}: ${m.grounds}`,
                                  `${t("search.period")}: ${m.sanction_start || "—"} → ${!m.sanction_end || m.sanction_end >= "2999" ? t("search.ongoing") : m.sanction_end}`].filter(Boolean).join(" · ")}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-slate-600">{t(`search.et_${m.entry_type}`)}</td>
                          <td className="px-3 py-2.5 text-slate-600">{m.program || "—"}</td>
                          <td className="px-3 py-2.5 text-slate-500 font-mono text-xs">
                            {m.url ? <a href={m.url} target="_blank" rel="noreferrer" className="text-[#044CBC] hover:underline" data-testid="match-official-link">{m.reference || t("search.official_link")}</a> : (m.reference || "—")}
                          </td>
                          <td className="px-3 py-2.5 text-right">
                            <span className={`font-mono font-bold ${m.score >= 90 ? "text-red-600" : m.score >= 80 ? "text-amber-600" : "text-slate-600"}`}>{m.score}%</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="ec-card p-6 space-y-5" data-testid="bulk-search-panel">
          <div>
            <label className="ec-label">{t("search.bulk")}</label>
            <p className="text-xs text-slate-500 mb-2">{t("search.bulk_help")}</p>
            <textarea className="ec-input font-mono" rows={7} value={bulkText} onChange={(e) => setBulkText(e.target.value)} placeholder={"Juan Pérez\nEmpresa S.A.\nEric Badege"} data-testid="bulk-names-input" />
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <select className="ec-input w-auto" value={entityType} onChange={(e) => setEntityType(e.target.value)} data-testid="bulk-entity-type-select">
              <option value="all">{t("search.t_all")}</option>
              <option value="person">{t("search.t_person")}</option>
              <option value="entity">{t("search.t_entity")}</option>
            </select>
            {!fixed && <SourcePicker sources={sources} setSources={setSources} visible={VISIBLE} />}
            <button onClick={runBulk} disabled={bulkBusy} data-testid="bulk-search-button" className="flex items-center gap-2 px-8 py-3.5 rounded-lg bg-[#044CBC] text-white text-base font-extrabold shadow-lg shadow-orange-300/50 hover:bg-[#033A8C] hover:shadow-xl hover:shadow-orange-300/60 hover:-translate-y-0.5 transition-all disabled:opacity-60">
              {bulkBusy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
              {bulkBusy ? t("search.running") : t("search.bulk_run")}
            </button>
          </div>
          {bulkResults.length > 0 && (
            <div className="flex justify-end">
              <button onClick={newSearch} data-testid="bulk-new-search" className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-slate-300 bg-white text-xs font-bold text-slate-700 hover:border-slate-500 transition-colors">
                <RotateCcw className="w-3.5 h-3.5" /> {t("search.new")}
              </button>
            </div>
          )}
          {bulkResults.length > 0 && (
            <div className="overflow-x-auto rounded-lg border border-slate-200" data-testid="bulk-results-table">
              <table className="w-full text-sm min-w-[640px]">
                <thead>
                  <tr className="bg-[#16335F] text-white text-left">
                    <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("search.col_query")}</th>
                    <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("search.consult_no")}</th>
                    <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("common.result")}</th>
                    <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide text-right">{t("common.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {bulkResults.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`bulk-result-${r.consult_number}`}>
                      <td className="px-3 py-2.5 font-semibold text-slate-800">{r.query}</td>
                      <td className="px-3 py-2.5 font-mono text-xs text-slate-500">{r.consult_number}</td>
                      <td className="px-3 py-2.5">
                        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${r.result === "match" ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}>
                          {r.result === "match" ? `${t("search.match")} (${r.match_count})` : t("search.no_match")}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <button onClick={() => downloadFile(`/sanctions/searches/${r.id}/pdf`, `consulta_${r.consult_number}.pdf`)} data-testid={`bulk-pdf-${r.consult_number}`} className="text-[#044CBC] hover:text-[#033A8C] font-semibold text-xs inline-flex items-center gap-1">
                          <Download className="w-3.5 h-3.5" /> PDF
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
